import { Check } from '@phosphor-icons/react';

import { MoneyDisplay } from '../common/MoneyDisplay';
import pt from '../../locales/pt';
import {
  firstVictoryMonthsFromPlan,
  formatFreeEta,
  maxPayoffMonthsFromPlan,
} from '../../lib/debtDisplayUtils';
import { cn } from '../../utils/cn';

const METHODS = ['avalanche', 'snowball'];

function impactFromPlan(plan, methodId) {
  if (!plan) return { freeIn: '—', secondaryLabel: null, secondaryValue: '—' };
  const months = maxPayoffMonthsFromPlan(plan);
  const freeIn = months != null ? formatFreeEta(months) : '—';

  if (methodId === 'snowball') {
    const first = firstVictoryMonthsFromPlan(plan);
    return {
      freeIn,
      secondaryLabel: pt.liabilities.plan.firstVictory,
      secondaryValue: first != null ? formatFreeEta(first) : '—',
      secondaryIsMoney: false,
    };
  }

  const interest =
    plan.total_interest_remaining_eur != null
      ? Number(plan.total_interest_remaining_eur)
      : null;
  return {
    freeIn,
    secondaryLabel: pt.liabilities.plan.totalInterest,
    secondaryValue: interest,
    secondaryIsMoney: true,
  };
}

/**
 * Benefit-first method picker (jargon only as a small note).
 * @param {object} [impacts] — { avalanche: planData, snowball: planData }
 */
export function DebtMethodPicker({ value, onChange, impacts = {} }) {
  return (
    <div className="flex flex-col gap-3">
      {METHODS.map((id) => {
        const meta = pt.liabilities.repaymentWizard.methods[id];
        const selected = value === id;
        const impact = impactFromPlan(impacts[id], id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={cn(
              'rounded-[18px] border-[1.5px] p-[15px] text-left transition-colors',
              selected
                ? 'border-sage-500 bg-card shadow-[0_0_0_3px_rgba(244,247,240,1)] dark:shadow-[0_0_0_3px_rgba(74,98,67,0.25)]'
                : 'border-border bg-card hover:border-sage-300/60',
            )}
          >
            <div className="flex items-center gap-2.5">
              <span
                className={cn(
                  'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2',
                  selected
                    ? 'border-sage-600 bg-sage-600 text-white'
                    : 'border-stone-300 text-transparent dark:border-stone-600',
                )}
              >
                <Check className="h-3 w-3" weight="bold" aria-hidden />
              </span>
              <span className="flex-1 font-display text-base font-semibold text-foreground">
                {meta.title}
              </span>
            </div>
            <p className="mt-2 text-[12.5px] leading-relaxed text-muted-foreground">
              {meta.description}{' '}
              <span className="text-muted-foreground/70">{meta.methodNote}</span>
            </p>
            {selected ? (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-sage-50 px-3 py-2.5 dark:bg-sage-950/40">
                  <p className="text-[10.5px] font-semibold text-sage-800 dark:text-sage-300">
                    {pt.liabilities.plan.freeIn}
                  </p>
                  <p className="mt-0.5 font-display text-[15px] font-bold text-foreground">
                    {impact.freeIn}
                  </p>
                </div>
                <div className="rounded-xl bg-sage-50 px-3 py-2.5 dark:bg-sage-950/40">
                  <p className="text-[10.5px] font-semibold text-sage-800 dark:text-sage-300">
                    {impact.secondaryLabel}
                  </p>
                  <p className="mt-0.5 font-display text-[15px] font-bold tabular-nums text-foreground">
                    {impact.secondaryIsMoney && impact.secondaryValue != null ? (
                      <MoneyDisplay value={impact.secondaryValue} size="sm" />
                    ) : (
                      impact.secondaryValue ?? '—'
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-2.5 flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span>
                  {pt.liabilities.plan.freeIn}{' '}
                  <b className="font-semibold text-foreground">{impact.freeIn}</b>
                </span>
                <span>
                  {impact.secondaryLabel}{' '}
                  <b className="font-semibold tabular-nums text-foreground">
                    {impact.secondaryIsMoney && impact.secondaryValue != null ? (
                      <MoneyDisplay value={impact.secondaryValue} size="sm" />
                    ) : (
                      impact.secondaryValue ?? '—'
                    )}
                  </b>
                </span>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default DebtMethodPicker;
